/**
 * sp2Payload.js — SP2.0 → Beat Arranger import handler
 *
 * Reads import payload from the URL fragment first, then query params. Supports:
 *   - #ltbb_import=<base64> (The Booth and current private handoff)
 *   - ?ltbb_import=<base64> (current SP2.0 format, new shape)
 *   - ?import=<base64> (legacy format)
 *   - ?payload=<base64> (legacy format)
 *   - ?sp2=<base64> (legacy format)
 *
 * New SP2.0 payload shape:
 *   { tracks: [{id, name, url, bpm, key, instrument_group, bars, style_tags}],
 *     sessionBpm, sessionKey, knockEngine: {...} | null, source: 'sp2', timestamp }
 *
 * Legacy payload shape:
 *   { loops: [{id, name, bpm, key, instrument_group, bars, energy, pack_code, audio_url}],
 *     session_bpm, reflect_session_id, source, timestamp }
 */

import { INSTRUMENT_COLORS } from '@/utils/constants';
import { snapToGrid } from '@/state/useSnapGrid';
import { cacheAudioUrl } from '@/api/loops';
import { soundSettings } from '@/audio/knockRender';
import { normalizePatternTracks } from '@/audio/patternModel';
import { defaultTrackEffects } from '@/state/trackModel';
import { normalizeLyrics } from '@/lyrics/schema';
import { layoutSections } from '@/lyrics/markers';

export const MAX_IMPORT_SIZE = 24 * 1024;
export const PENDING_IMPORT_KEY = 'ba_pending_import';

function hashParams(location) {
  const raw = (location.hash || '').replace(/^#/, '');
  return new URLSearchParams(raw);
}

function importCandidate(location, storage) {
  const hash = hashParams(location);
  const query = new URLSearchParams(location.search || '');
  const hashValue = hash.get('ltbb_import');
  if (hashValue) return { encoded: hashValue, source: 'hash', key: 'ltbb_import' };
  const queryKey = ['ltbb_import', 'import', 'payload', 'sp2'].find((key) => query.get(key));
  if (queryKey) return { encoded: query.get(queryKey), source: 'query', key: queryKey };
  const pending = storage?.getItem(PENDING_IMPORT_KEY);
  return pending ? { encoded: pending, source: 'pending', key: 'ltbb_import' } : null;
}

function decodePayload(encoded) {
  if (encoded.length > MAX_IMPORT_SIZE) throw new Error('Import payload is too large');
  const standard = encoded.replace(/ /g, '+').replace(/-/g, '+').replace(/_/g, '/');
  const padded = standard.padEnd(Math.ceil(standard.length / 4) * 4, '=');
  const binary = atob(padded);
  const percentEncoded = Array.from(
    binary,
    (character) => `%${character.charCodeAt(0).toString(16).padStart(2, '0')}`,
  ).join('');
  return JSON.parse(decodeURIComponent(percentEncoded));
}

function lyricsFromBooth(booth) {
  return normalizeLyrics({
    version: booth.version,
    title: booth.title ?? '',
    style: booth.style ?? booth.vibe ?? '',
    cadence: Array.isArray(booth.cadence) ? booth.cadence : [],
    sections: booth.sections,
  });
}

function stripImport(candidate, location, history) {
  if (candidate.source === 'pending') return;
  const url = new URL(location.href);
  if (candidate.source === 'hash') {
    const params = hashParams(location);
    params.delete('ltbb_import');
    const remaining = params.toString();
    url.hash = remaining ? `#${remaining}` : '';
  } else if (candidate.key === 'ltbb_import') {
    url.searchParams.delete('ltbb_import');
  }
  history.replaceState(history.state, '', `${url.pathname}${url.search}${url.hash}`);
}

/**
 * Check if any SP2 import param exists in the current URL.
 */
export function hasSP2Import() {
  return Boolean(importCandidate(window.location, window.sessionStorage));
}

/**
 * Parse the SP2.0 payload from URL search params.
 * Handles both new and legacy payload formats.
 */
export function parseSP2Payload({
  location = window.location,
  history = window.history,
  storage = window.sessionStorage,
} = {}) {
  let candidate = null;
  try {
    candidate = importCandidate(location, storage);
    if (!candidate) return null;
    const data = decodePayload(candidate.encoded);

    // Detect payload format
    if (data.tracks && Array.isArray(data.tracks)) {
      if (data.source === 'booth' && !data.booth) throw new Error('Missing Booth lyrics');
      const parsed = { format: 'new', ...data };
      if (data.booth) parsed.lyrics = lyricsFromBooth(data.booth);
      if (candidate.source !== 'pending') storage.setItem(PENDING_IMPORT_KEY, candidate.encoded);
      stripImport(candidate, location, history);
      return parsed;
    }

    if (data.loops && Array.isArray(data.loops)) {
      const parsed = { format: 'legacy', ...data };
      if (candidate.source !== 'pending') storage.setItem(PENDING_IMPORT_KEY, candidate.encoded);
      stripImport(candidate, location, history);
      return parsed;
    }

    return null;
  } catch (e) {
    if (candidate?.source === 'pending') storage.removeItem(PENDING_IMPORT_KEY);
    return null;
  }
}

export function clearPendingImport(storage = window.sessionStorage) {
  storage.removeItem(PENDING_IMPORT_KEY);
}

/**
 * Convert parsed payload into Beat Arranger tracks + settings.
 * Handles both new and legacy formats.
 */
export function payloadToTracks(payload) {
  if (!payload) return null;

  const result = payload.format === 'new' ? convertNewFormat(payload) : convertLegacyFormat(payload);
  if (!payload.lyrics) return { ...result, tracks: normalizePatternTracks(result.tracks) };
  const layout = layoutSections(payload.lyrics.sections);
  const tracks = result.tracks.map((track) => ({
    ...track,
    clips: track.clips.map((clip) => ({ ...clip, bars: layout.totalBars })),
  }));
  return {
    ...result,
    tracks: normalizePatternTracks(tracks),
    markers: layout.markers,
    lyrics: { ...payload.lyrics, sections: layout.sections },
    totalBars: layout.totalBars,
    overflowCount: layout.overflowCount,
  };
}

function convertNewFormat(payload) {
  const tracks = [];

  // Convert each track from SP2.0 format to Beat Arranger track
  if (payload.tracks?.length) {
    payload.tracks.forEach((track, index) => {
      const instrument = track.instrument_group || 'melody';
      const color = INSTRUMENT_COLORS[instrument] || '#666';

      // Cache the audio URL for playback
      if (track.url) {
        cacheAudioUrl(track.id, track.url);
      }

      const freshIds = payload.source === 'booth';
      tracks.push({
        id: freshIds ? `booth-track-${Date.now()}-${index}` : track.id || `sp2-track-${Date.now()}-${index}`,
        name: track.name,
        instrument,
        color,
        volume: 80,
        pan: 0,
        muted: false,
        solo: false,
        effects: defaultTrackEffects(),
        clips: [{
          id: `${freshIds ? 'booth' : 'sp2'}-clip-${Date.now()}-${index}`,
          loop_id: track.id,
          name: track.name,
          color,
          start_bar: snapToGrid(0),
          bars: track.bars || 4,
          sourceBpm: track.bpm,
          naturalLoopBars: track.bars || 4,
          isWholeLoop: true,
          fade_in: 0,
          fade_out: 0,
          audio_url: track.url || null,
          style_tags: Array.isArray(track.style_tags) ? track.style_tags : [],
          key: track.key || null,
        }],
      });
    });
  }

  // Add Knock Engine bass if active
  if (payload.knockEngine?.active) {
    const ke = payload.knockEngine;
    const color = INSTRUMENT_COLORS['bass'] || '#39FF14';
    const uniqueId = `knock-${Date.now()}-${tracks.length}`;
    const settings = soundSettings({ ...soundSettings(ke.sound_type), oscType: ke.osc_type,
      filterFreq: ke.filter_freq, detune: ke.detune });

    tracks.push({
      id: `track-${uniqueId}`,
      name: `Knock Bass (${ke.feel || 'Basic'} \u2022 ${ke.sound_type || 'Sub Sine'})`,
      instrument: 'bass',
      color,
      volume: 80,
      pan: 0,
      muted: false,
      solo: false,
      effects: defaultTrackEffects(),
      clips: [{
        id: `knock-clip-${Date.now()}`,
        loop_id: uniqueId,
        name: `Knock Bass (${ke.feel || 'Basic'})`,
        color,
        start_bar: snapToGrid(0),
        bars: ke.bars || 4,
        sourceBpm: ke.bpm || payload.sessionBpm,
        naturalLoopBars: ke.bars || 4,
        isWholeLoop: true,
        fade_in: 0,
        fade_out: 0,
        audio_url: null, // Synthesized, no audio file
        isKnockEngine: true,
        knockPattern: ke.pattern || [],
        knockOscType: settings.oscType,
        knockFilterFreq: settings.filterFreq,
        knockDetune: settings.detune,
        knockVolume: ke.volume ?? 0.8,
        knockFeel: ke.feel,
        knockSoundType: ke.sound_type,
        knockSampleId: ke.sample_id || null,
        knockLockToKey: ke.lock_to_key !== false,
        knockKey: ke.key || payload.sessionKey || null,
        knockGenre: ke.genre || 'default',
      }],
    });
  }

  return {
    tracks,
    bpm: payload.sessionBpm || 90,
    key: payload.sessionKey || null,
    reflectSessionId: null,
  };
}

function convertLegacyFormat(payload) {
  const tracks = payload.loops.map((loop, index) => {
    const instrument = loop.instrument || loop.instrument_group || 'melody';
    const color = INSTRUMENT_COLORS[instrument] || '#666';

    if (loop.audio_url) {
      cacheAudioUrl(loop.id, loop.audio_url);
    }

    return {
      id: `sp2-track-${Date.now()}-${index}`,
      name: loop.name,
      instrument,
      color,
      volume: 80,
      pan: 0,
      muted: false,
      solo: false,
      effects: defaultTrackEffects(),
      clips: [{
        id: `sp2-clip-${Date.now()}-${index}`,
        loop_id: loop.id,
        name: loop.name,
        color,
        start_bar: snapToGrid(0),
        bars: loop.bars || 4,
        sourceBpm: loop.bpm,
        naturalLoopBars: loop.bars || 4,
        isWholeLoop: true,
        fade_in: 0,
        fade_out: 0,
        audio_url: loop.audio_url || null,
        style_tags: Array.isArray(loop.style_tags) ? loop.style_tags : [],
        key: loop.key || null,
      }],
    };
  });

  return {
    tracks,
    bpm: payload.session_bpm || 90,
    key: null,
    reflectSessionId: payload.reflect_session_id || null,
  };
}
